import { Link } from 'react-router-dom';
import { ROLE_LABELS, TENANT_MAX_MEMBERS } from '../../api/types';
import { useMe } from '../../auth/AuthContext';
import { Alert, Badge, Card, PageHeader, formatDate } from '../../components/ui';
import { CONTACT_EMAIL, DEMO_URL } from '../../links';

/**
 * What a signed-in account with no organisation sees at /portal: the
 * verdict ("No organisation yet"), the one path to access, and a look at
 * the demo. The hub has no self-serve path: Balsam staff onboard
 * organisations, owners add accounts to theirs.
 */
export function Welcome() {
  const me = useMe();
  const fullName = [me.user.firstName, me.user.lastName].filter(Boolean).join(' ');
  const demoHost = new URL(DEMO_URL).host;
  const subject = 'Balsam Hub: access for my organization';
  const body = [
    'Hello Balsam,',
    '',
    'Please onboard my organization on Balsam Hub.',
    '',
    'Organization: ',
    `Hub email: ${me.user.email}`,
  ].join('\n');
  const requestHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;

  return (
    <>
      <PageHeader eyebrow="Portal" title="Welcome to Balsam Hub" />

      {!me.user.isEmailVerified || (me.user.phone && !me.user.isPhoneVerified) ? (
        <Alert kind="info">
          {!me.user.isEmailVerified ? 'Your email address is not verified yet. ' : null}
          {me.user.phone && !me.user.isPhoneVerified ? (
            <>
              Verify your phone number to enable sign-in by text message.{' '}
              <Link to="/account">Verify now</Link>
            </>
          ) : null}
        </Alert>
      ) : null}

      <section className="verdict" aria-labelledby="verdict-title">
        <p className="verdict__status">
          <Badge tone="warn">No organisation yet</Badge>
        </p>
        <h2 className="verdict__title" id="verdict-title">
          Your account is ready; it is not part of an organisation yet
        </h2>
        <p className="verdict__detail">
          Balsam staff onboard new organisations and make this account the owner. If yours is
          already onboarded, ask its owner to add you; an organisation can have up to{' '}
          {TENANT_MAX_MEMBERS} accounts, owner included.
        </p>
        <p className="verdict__actions">
          <a className="btn btn--primary" href={requestHref}>
            Email Balsam
          </a>
          <a
            className="btn btn--secondary"
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the demo
          </a>
          <span className="muted">
            The demo opens {demoHost} in a new tab and touches nothing here.
          </span>
        </p>
      </section>

      <Card title="Your account" actions={<Link to="/account">Account &amp; security</Link>}>
        <dl className="kv">
          <dt>Name</dt>
          <dd>{fullName || <span className="muted">Not set</span>}</dd>
          <dt>Email</dt>
          <dd>
            {me.user.email}{' '}
            {me.user.isEmailVerified ? (
              <Badge tone="ok">Verified</Badge>
            ) : (
              <Badge tone="warn">Unverified</Badge>
            )}
          </dd>
          <dt>Role</dt>
          <dd>{ROLE_LABELS[me.principal.role]}</dd>
          <dt>Member since</dt>
          <dd>{formatDate(me.user.createdAt)}</dd>
        </dl>
      </Card>
    </>
  );
}
